import { assert, unreachable } from '@chainflip/utils/assertion';
import { chainflipAssets, type ChainflipAsset } from '@chainflip/utils/chainflip';
import { z } from 'zod';
import {
  type StrategyInfoFragment,
  type StrategyType,
} from '@/shared/graphql/generated/reporting/graphql';
import TokenAmount from './TokenAmount';

export type StrategyTemplateKey = `${ChainflipAsset},${ChainflipAsset},${StrategyType},${string}`;

export type StrategyTemplate = {
  templateKey: StrategyTemplateKey;
  baseAsset: ChainflipAsset;
  quoteAsset: ChainflipAsset;
  type: StrategyType;
  instanceCount: number;
  totalBaseLiquidity: TokenAmount;
  totalQuoteLiquidity: TokenAmount;
  totalBaseLiquidityUsd: number;
  totalQuoteLiquidityUsd: number;
  ownInstance: TradingStrategy | undefined;
  reportingData:
    | {
        totalFilledVolumeUsd: number;
        apyPercent: number;
        pnl24HourUsd: number;
      }
    | undefined;
};

export type TradingStrategy = {
  templateKey: StrategyTemplateKey;
  baseAsset: ChainflipAsset;
  quoteAsset: ChainflipAsset;
  type: StrategyType;
  baseLiquidity: TokenAmount;
  quoteLiquidity: TokenAmount;
  baseLiquidityUsd: number;
  quoteLiquidityUsd: number;
  totalLiquidityUsd: number;
  strategyAccountSs58Id: string;
  ownerAccountSs58Id: string;
  status: 'ACTIVE' | 'CLOSED';
  reportingData:
    | {
        filledVolumeUsd: number;
        pnlUsd: number;
        pnl24HourUsd: number;
        apyPercent: number;
        projectedReturnUsd: number;
        createdAtBlockTimestamp?: string;
        closedAtBlockTimestamp?: string;
        closedAtBlockId?: number;
        closedAtEventIndex?: number;
      }
    | undefined;
};

export const getInventoryBasedStrategyKey = ({
  minBuyTick,
  maxBuyTick,
  minSellTick,
  maxSellTick,
}: {
  minBuyTick: number;
  maxBuyTick: number;
  minSellTick: number;
  maxSellTick: number;
}) => `${minBuyTick}_${maxBuyTick}_${minSellTick}_${maxSellTick}`;

export const getStrategyBuyAndSellTick = (type: StrategyType, key: string) => {
  switch (type) {
    case 'TICK_ZERO_CENTERED':
    case 'SIMPLE_BUY_SELL': {
      const [buyTick, sellTick] = key.split('_');
      return {
        minBuyTick: Number(buyTick),
        maxBuyTick: Number(buyTick),
        minSellTick: Number(sellTick),
        maxSellTick: Number(sellTick),
      };
    }
    case 'INVENTORY_BASED': {
      const [minBuyTick, maxBuyTick, minSellTick, maxSellTick] = key.split('_');
      return {
        minBuyTick: Number(minBuyTick),
        maxBuyTick: Number(maxBuyTick),
        minSellTick: Number(minSellTick),
        maxSellTick: Number(maxSellTick),
      };
    }
    default:
      unreachable(type, `Unsupported strategy type: ${type}`);
  }
};

const findMatchingValue = <T extends string>(
  possibilities: readonly T[],
  value: string,
): T | undefined => possibilities.find((item) => item.toLowerCase() === value.toLowerCase());

const strategyTypes = ['INVENTORY_BASED', 'SIMPLE_BUY_SELL', 'TICK_ZERO_CENTERED'] as const;

export const templateKeySchema = z
  .string()
  .regex(/^[a-zA-Z0-9]+,[a-zA-Z0-9]+,[a-zA-Z_]+,[0-9-_]+$/, 'Invalid strategy key format')
  .transform((item) => {
    const parts = item.split(',');
    if (parts.length !== 4) {
      throw new Error('Invalid strategy template key format');
    }
    const baseAsset = findMatchingValue(chainflipAssets, parts[0]);
    assert(baseAsset && baseAsset !== 'Usdc', 'Invalid base asset');
    const quoteAsset = findMatchingValue(chainflipAssets, parts[1]);
    assert(quoteAsset === 'Usdc', 'Invalid quote asset');
    const type = findMatchingValue(strategyTypes, parts[2]);
    assert(type, 'Invalid strategy type');
    const key = parts[3];
    const { minBuyTick, minSellTick, maxBuyTick, maxSellTick } = getStrategyBuyAndSellTick(
      type,
      key,
    );
    return { baseAsset, quoteAsset, minBuyTick, minSellTick, maxBuyTick, maxSellTick, type, key };
  });

export type ParsedTemplateKey = z.output<typeof templateKeySchema>;

export const getStrategyTemplateUrl = (template: StrategyTemplate | TradingStrategy) =>
  `/strategies/templates/${template.templateKey}`;

export const getStrategyTemplateKey = (
  strategy: Pick<StrategyInfoFragment, 'baseAsset' | 'key' | 'type'>,
) => `${strategy.baseAsset},Usdc,${strategy.type},${strategy.key}` as StrategyTemplateKey;

export const calculateAnnualReturn = (
  totalLiquidityUsd: number | string,
  apyPercent: number | string,
) => Number(totalLiquidityUsd) * (Number(apyPercent) / 100);

export function getStrategyFromReportingFragment(
  fragment: StrategyInfoFragment | undefined,
): TradingStrategy | undefined;
export function getStrategyFromReportingFragment(fragment: StrategyInfoFragment): TradingStrategy;
export function getStrategyFromReportingFragment(
  fragment: StrategyInfoFragment | undefined,
): TradingStrategy | undefined {
  return (
    fragment && {
      ...fragment,
      templateKey: getStrategyTemplateKey(fragment),
      baseLiquidity: TokenAmount.fromAsset(fragment.baseLiquidity, fragment.baseAsset),
      quoteLiquidity: TokenAmount.fromAsset(fragment.quoteLiquidity, fragment.quoteAsset),
      baseLiquidityUsd: Number(fragment.baseLiquidityValueUsd),
      quoteLiquidityUsd: Number(fragment.quoteLiquidityValueUsd),
      totalLiquidityUsd: Number(fragment.totalLiquidityValueUsd),
      reportingData: {
        filledVolumeUsd: Number(fragment.filledVolumeValueUsd),
        pnlUsd: Number(fragment.pnlValueUsd),
        pnl24HourUsd: Number(fragment.pnl24HourValueUsd),
        apyPercent: Number(fragment.apy7Days),
        projectedReturnUsd: calculateAnnualReturn(
          fragment.totalLiquidityValueUsd,
          fragment.apy7Days,
        ),
        createdAtBlockTimestamp: fragment.createdAtBlockTimestamp ?? undefined,
        closedAtBlockTimestamp: fragment.closedAtBlockTimestamp ?? undefined,
        closedAtBlockId: fragment.closedAtBlockId ?? undefined,
        closedAtEventIndex: fragment.closedAtEventIndex ?? undefined,
      },
    }
  );
}
